import type { LandingPageContent } from './types'

// GitLab-Issue #143 (Sub-Issue von #142): Schritt-für-Schritt-How-to-Seite für Kunden. Jeder
// Schritt und jeder Screenshot wurde beim Schreiben dieser Datei live gegen die QA-Docker-Umgebung
// nachvollzogen (frischer Test-Account, echte Anfrage #30) - siehe Issue-Kommentar für den
// QA-Bericht. Screenshots liegen unter public/how-to/kunde/, mit Test-/Platzhalterdaten (kein
// echtes Haus, Passwörter/Recovery-Codes aus den Screenshots sind bereits verfallene QA-Testdaten).
export const SO_FUNKTIONIERT_ES_FUER_KUNDEN: LandingPageContent = {
  slug: 'so-funktioniert-es-fuer-kunden',
  lang: 'de',
  topicId: 'so-funktioniert-es-fuer-kunden',
  title: 'So funktioniert Roofora für Kunden – Anleitung Schritt für Schritt',
  metaDescription:
    'Schritt-für-Schritt-Anleitung für Kunden: Account erstellen, Anfrage mit Dach- und ' +
    'Zählerschrankfotos veröffentlichen, Angebote vergleichen und Kontakt gezielt freigeben - mit ' +
    'echten Screenshots aus der Anwendung.',
  keywords: [
    'Roofora Anleitung',
    'Roofora wie funktioniert',
    'Photovoltaik Anfrage erstellen Anleitung',
    'Solaranlage Angebote vergleichen wie geht das',
    'Kontakt freigeben Handwerker Solaranlage',
  ],
  eyebrow: 'Anleitung für Kunden',
  h1: 'So funktioniert Roofora für Kunden',
  intro:
    'Diese Anleitung zeigt jeden Schritt aus Kundensicht - von der ersten Anmeldung bis zur ' +
    'Kontaktfreigabe an einen Handwerksbetrieb - mit echten Screenshots aus der Anwendung. Sie ' +
    'brauchen dafür kein technisches Vorwissen.',
  sections: [
    { type: 'heading', text: 'In 3 Minuten erklärt' },
    {
      type: 'paragraph',
      text:
        'Sie erstellen einen anonymen Account, laden Fotos Ihres Dachs und Zählerschranks hoch und ' +
        'veröffentlichen daraus eine anonymisierte Anfrage. Handwerksbetriebe aus Ihrer Region geben ' +
        'darauf unabhängig voneinander ein Angebot ab. Sie vergleichen die Preise und geben Ihre ' +
        'Kontaktdaten erst dann frei, wenn Sie sich für einen einzelnen Betrieb entschieden haben.',
    },
    { type: 'heading', text: 'Was brauche ich vorher?' },
    {
      type: 'list',
      items: [
        'Ein bis drei Fotos Ihres Dachs bzw. Hausdachs (Handyfotos reichen)',
        'Ein Foto Ihres Zählerschranks',
        'Ihre Postleitzahl',
        'Ungefähre Angaben zu Gebäude, Dach und Stromverbrauch - vieles ist bereits sinnvoll vorbelegt',
        'Keine E-Mail-Adresse und keine Telefonnummer nötig, nur einen selbst gewählten Username und ein Passwort',
      ],
    },
  ],
  steps: [
    {
      title: 'Einstieg auf der Startseite',
      whatYouSee:
        'Im Hero-Bereich von roofora.de zwei große Buttons - "Kostenlos Angebote erhalten" (Kunde) ' +
        'und "Als Handwerker Aufträge finden" (Handwerker) - darunter eine Liste mit Vertrauens-' +
        'Argumenten wie "Account ganz ohne E-Mail & Telefonnummer".',
      whatToDo: 'Klicken Sie auf "Kostenlos Angebote erhalten".',
      whatHappensNext: 'Sie gelangen auf die Kunden-Registrierungsseite mit den Reitern "Registrieren" und "Anmelden".',
      successCriterion: 'Die Seite "Account erstellen" mit den Feldern Username und Passwort ist sichtbar.',
      screenshot: {
        src: '/how-to/kunde/01-hero-cta.jpg',
        alt: 'Roofora-Startseite mit den Buttons "Kostenlos Angebote erhalten" und "Als Handwerker Aufträge finden"',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Anonymen Account registrieren',
      whatYouSee:
        'Ein Formular mit den Feldern Username (mit Link "Zufälligen Username generieren") und ' +
        'Passwort, dazu der Hinweis, dass Sie nach der Registrierung einen Recovery-Code erhalten.',
      whatToDo:
        'Lassen Sie sich einen Username generieren oder vergeben Sie selbst einen, wählen Sie ein ' +
        'Passwort mit mindestens 8 Zeichen und klicken Sie auf "Account erstellen".',
      whatHappensNext: 'Der Username wird live auf Verfügbarkeit geprüft ("✓ Username ist verfügbar").',
      successCriterion: 'Beide Felder sind ausgefüllt, der Button "Account erstellen" lässt sich anklicken.',
      troubleshooting:
        'Ist der gewählte Username bereits vergeben, erscheint sofort ein Hinweis - probieren Sie ' +
        'einfach einen anderen oder lassen Sie sich per Klick einen neuen vorschlagen.',
      screenshot: {
        src: '/how-to/kunde/02-registrierung.jpg',
        alt: 'Ausgefülltes Registrierungsformular mit Username und Passwort, Hinweis "Username ist verfügbar"',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Recovery-Code sichern',
      whatYouSee:
        'Username, Passwort (maskiert, über "Anzeigen" einblendbar) und ein Recovery-Code, dazu die ' +
        'Optionen "Kopieren" und "Als .txt herunterladen".',
      whatToDo:
        'Speichern Sie mindestens den Recovery-Code sicher, zum Beispiel per Download, und bestätigen ' +
        'Sie die Checkbox "Ich habe Passwort und Recovery-Code gespeichert".',
      whatHappensNext: 'Erst nach dem Bestätigen lässt sich der Button "Weiter zur Plattform" anklicken.',
      successCriterion: 'Sie gelangen in Ihr Kunden-Dashboard.',
      troubleshooting:
        'Es gibt keinen Passwort-Reset per E-Mail - ohne Recovery-Code ist der Account bei einem ' +
        'verlorenen Passwort nicht wiederherstellbar. Bewahren Sie ihn deshalb wirklich sicher auf.',
      screenshot: {
        src: '/how-to/kunde/03-account-bereit.jpg',
        alt: 'Bestätigungsseite "Dein Account ist bereit" mit Username, Passwort und Recovery-Code',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Dashboard-Übersicht',
      whatYouSee:
        'Ihren Username, die Kunden-ID sowie drei Kacheln - "Mein Profil", "Meine Anfragen" und ' +
        '"Meine Kontaktfreigaben" - außerdem den Button "+ Neue Anfrage erstellen".',
      whatToDo: 'Klicken Sie auf "+ Neue Anfrage erstellen", um Ihre erste Solaranfrage zu starten.',
      whatHappensNext: 'Der 6-Schritte-Wizard "Auftrag anlegen" öffnet sich.',
      successCriterion: 'Die Reiter "1. Adresse & Haus" bis "6. Kontakt & Abschluss" sind oben sichtbar.',
      screenshot: {
        src: '/how-to/kunde/04-dashboard.jpg',
        alt: 'Kunden-Dashboard mit den Kacheln Mein Profil, Meine Anfragen, Meine Kontaktfreigaben',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Anfrage-Wizard: Adresse & Haus',
      whatYouSee:
        'Eine Live-Vorschau mit vorläufiger Anlagen-Empfehlung und Wirtschaftlichkeit, darunter ' +
        'Felder zu Auftragstyp, Land, Postleitzahl, Gebäudetyp, Baujahr, Dachform und Dachmaterial.',
      whatToDo:
        'Füllen Sie die Felder aus - viele sind bereits sinnvoll vorbelegt (z. B. "Komplettsystem mit ' +
        'Montage/Installation", "Satteldach"). Pflicht ist mindestens die Postleitzahl.',
      whatHappensNext: 'Die Live-Vorschau aktualisiert sich automatisch mit jeder Eingabe.',
      successCriterion: 'Alle Pflichtfelder sind ausgefüllt, der Button "Weiter" führt zu Schritt 2 "Dach".',
      troubleshooting: 'Ohne Postleitzahl lässt sich der Wizard nicht fortsetzen - das steht direkt im Formular.',
      screenshot: {
        src: '/how-to/kunde/05-wizard-start.jpg',
        alt: 'Wizard-Schritt "Adresse & Haus" mit Live-Vorschau und Auftragstyp-Auswahl',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Dachfotos hochladen',
      whatYouSee:
        'Für jede Dachfläche Felder zu Ausrichtung, Fläche und Neigung, darunter der Upload-Bereich ' +
        '"Dach-/Hausfotos" mit dem Zähler "0/3 Fotos".',
      whatToDo:
        'Klicken Sie auf "Dateien auswählen" und laden Sie ein bis drei Fotos Ihres Dachs bzw. ' +
        'Hauses hoch - Handyfotos reichen.',
      whatHappensNext:
        'Hochgeladene Fotos erscheinen als Miniaturansicht mit einem "×" zum Entfernen, der Zähler ' +
        'springt auf "1/3 Fotos".',
      successCriterion: 'Mindestens ein Dachfoto ist hochgeladen.',
      troubleshooting:
        'Ohne mindestens ein Foto lässt sich die Anfrage später nicht veröffentlichen - der Hinweis ' +
        'dazu steht direkt unter dem Upload-Feld.',
      screenshot: {
        src: '/how-to/kunde/06-dachfotos.jpg',
        alt: 'Upload-Bereich Dach-/Hausfotos mit einem hochgeladenen Foto, Zähler 1/3 Fotos',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Zählerschrank-Foto ergänzen',
      whatYouSee:
        'In Wizard-Schritt 3 "Installation" den Upload-Bereich "Zählerschrank-Fotos" sowie Fragen zu ' +
        'Kabelführung, Leerrohr und dem gewünschten Montageort für Wechselrichter/Speicher.',
      whatToDo:
        'Laden Sie mindestens ein Foto Ihres Zählerschranks hoch und beantworten Sie die technischen ' +
        'Fragen nach bestem Wissen - "Weiß ich nicht" ist überall eine gültige Antwort.',
      whatHappensNext: 'Der Foto-Zähler springt auf "1/2 Fotos".',
      successCriterion: 'Mindestens ein Zählerschrank-Foto ist hochgeladen.',
      troubleshooting: 'Auch dieses Foto ist Pflicht - ohne geht es beim Veröffentlichen nicht weiter.',
      screenshot: {
        src: '/how-to/kunde/07-zaehlerschrank-foto.jpg',
        alt: 'Upload-Bereich Zählerschrank-Fotos mit einem hochgeladenen Foto, Frage zur Kabelführung',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kontaktwunsch und Zeitfenster festlegen',
      whatYouSee:
        'In Wizard-Schritt 6 "Kontakt & Abschluss" die Auswahl "Kontaktierung erwünscht" (Ja/Nein) ' +
        'und darunter mehrere Zeitfenster von "8-10 Uhr" bis "20-22 Uhr".',
      whatToDo:
        'Wählen Sie "Ja", wenn Handwerksbetriebe Sie nach Ihrer individuellen Freigabe kontaktieren ' +
        'dürfen, und markieren Sie ein bevorzugtes Zeitfenster.',
      whatHappensNext:
        'Ihre Wahl wird nur an Handwerksbetriebe weitergegeben, die Sie später einzeln freigeben - ' +
        'nicht automatisch an alle, die Interesse zeigen.',
      successCriterion: 'Ein Zeitfenster ist markiert (orange hinterlegt).',
      screenshot: {
        src: '/how-to/kunde/08-kontakt-zeitfenster.jpg',
        alt: 'Auswahl Kontaktierung erwünscht (Ja) und bevorzugtes Zeitfenster 18-20 Uhr',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Anfrage veröffentlichen',
      whatYouSee:
        'Eine letzte Zusammenfassung mit Anlagen-Empfehlung, Wirtschaftlichkeit und Finanzierungs-' +
        'Rechner, darunter die Buttons "Entwurf speichern" und "Veröffentlichen".',
      whatToDo: 'Prüfen Sie die Zusammenfassung und klicken Sie auf "Veröffentlichen".',
      whatHappensNext:
        'Ihre Anfrage ist ab jetzt anonymisiert für Handwerksbetriebe in Ihrer Region sichtbar - ohne ' +
        'Ihre Kontaktdaten. Der Status wechselt zu "Veröffentlicht - Handwerker können Angebote abgeben".',
      successCriterion: 'Der grüne Status "Veröffentlicht" erscheint, die Angebotsfrist wird angezeigt.',
      troubleshooting:
        'Noch nicht fertig? "Entwurf speichern" sichert Ihre Eingaben, ohne die Anfrage schon für ' +
        'Handwerksbetriebe sichtbar zu machen.',
      screenshot: {
        src: '/how-to/kunde/09-veroeffentlicht.jpg',
        alt: 'Veröffentlichte Anfrage mit Status "Veröffentlicht - Handwerker können Angebote abgeben"',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Angebote vergleichen und Kontakt vorbereiten',
      whatYouSee:
        'Unter "Angebote (niedrigstes zuerst)" die abgegebenen Angebote mit Firmenname, Preis und ' +
        'der Kennzeichnung "BESTES ANGEBOT", dazu die Buttons "Angebot annehmen" und "Kostenlosen ' +
        'Kontakt zu diesem Solarteur herstellen" sowie eine Erklärung, dass Ihre Daten erst nach ' +
        'Zustimmung übermittelt werden.',
      whatToDo:
        'Vergleichen Sie die Preise und klicken Sie bei dem Betrieb, den Sie kontaktieren möchten, ' +
        'auf "Kostenlosen Kontakt zu diesem Solarteur herstellen".',
      whatHappensNext:
        'Ein Bestätigungsdialog erscheint - danach wird der Auftrag für andere Handwerksbetriebe ' +
        'nicht mehr angezeigt. Es entsteht dabei noch keine Vertragsbindung.',
      successCriterion: 'Der gewünschte Betrieb ist ausgewählt, die Box "Ihre Daten sind geschützt" ist sichtbar.',
      troubleshooting: 'Gefällt Ihnen kein Angebot, können Sie es stattdessen über "Ablehnen" aus der Liste entfernen.',
      screenshot: {
        src: '/how-to/kunde/10-angebot-kontakt-erklaerung.jpg',
        alt: 'Angebot mit Preis, Button "Kostenlosen Kontakt herstellen" und Erklärung zum Datenschutz',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kontaktdaten ergänzen',
      whatYouSee:
        'Ein Dialog "Kontaktdaten ergänzen" mit den Pflichtfeldern E-Mail und Telefon (mindestens ' +
        'eines von beiden) sowie optionalen Feldern für Name und Adresse.',
      whatToDo: 'Tragen Sie mindestens eine Erreichbarkeit ein und klicken Sie auf "Speichern und freigeben".',
      whatHappensNext:
        'Nur der ausgewählte Handwerksbetrieb sieht genau die Angaben, die Sie hier ausfüllen - keine ' +
        'automatische Weitergabe an andere Betriebe.',
      successCriterion: 'Der Dialog schließt sich und die Freigabe ist abgeschlossen.',
      troubleshooting: 'Ohne mindestens E-Mail oder Telefon lässt sich der Dialog nicht abschließen.',
      screenshot: {
        src: '/how-to/kunde/11-kontaktdaten-dialog.jpg',
        alt: 'Dialog Kontaktdaten ergänzen mit Feldern für E-Mail und Telefon',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kontakt erfolgreich freigegeben',
      whatYouSee:
        'Beim Angebot erscheint die grüne Meldung "Freigegeben - [Firmenname] kann Sie jetzt ' +
        'kontaktieren", zusätzlich der Button "Als PDF exportieren".',
      whatToDo: 'Nichts weiter - der Handwerksbetrieb meldet sich jetzt im von Ihnen gewählten Zeitfenster bei Ihnen.',
      whatHappensNext:
        'Der Auftrag ist für andere Betriebe nicht mehr sichtbar. Unter "Meine Kontaktfreigaben" im ' +
        'Dashboard sehen Sie alle freigegebenen Kontakte im Überblick.',
      successCriterion: 'Die grüne Erfolgsmeldung "Freigegeben" ist sichtbar.',
      screenshot: {
        src: '/how-to/kunde/12-freigegeben-erfolg.jpg',
        alt: 'Erfolgsmeldung "Freigegeben - kann Sie jetzt kontaktieren" beim ausgewählten Angebot',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: 'Brauche ich eine E-Mail-Adresse, um Roofora zu nutzen?',
      a:
        'Nein. Die Registrierung funktioniert vollständig anonym mit Username und Passwort. Ohne ' +
        'E-Mail-Adresse können wir Sie allerdings nicht aktiv benachrichtigen, wenn neue Angebote ' +
        'oder Kontaktanfragen eingehen - schauen Sie dafür regelmäßig in Ihrem Account vorbei, oder ' +
        'hinterlegen Sie optional eine E-Mail-Adresse.',
    },
    {
      q: 'Kostet die Nutzung als Kunde etwas?',
      a: 'Nein, Anfrage erstellen, Angebote erhalten und Kontakt freigeben ist für Kunden aktuell kostenlos.',
    },
    {
      q: 'Muss ich das günstigste Angebot annehmen?',
      a:
        'Nein. Sie entscheiden frei, ob und welchen Handwerksbetrieb Sie kontaktieren - die ' +
        'Kontaktfreigabe bedeutet nur, dass Sie sich für diesen Betrieb entscheiden, nicht dass ein ' +
        'Vertrag zustande kommt.',
    },
    {
      q: 'Was passiert, wenn kein Handwerksbetrieb ein Angebot abgibt?',
      a: 'Ihre Anfrage bleibt bis zum Ablauf der Angebotsfrist sichtbar. Sie können sie jederzeit bearbeiten.',
    },
    {
      q: 'Wie viele Handwerksbetriebe dürfen meine Kontaktdaten bekommen?',
      a:
        'Sie geben jeden Betrieb einzeln frei - eine Obergrenze pro Anfrage (in der Anfrage-Übersicht ' +
        'als "max. Kontaktfreigaben" angezeigt) verhindert, dass zu viele Betriebe gleichzeitig ' +
        'Kontakt aufnehmen.',
    },
  ],
  primaryCta: { label: 'Jetzt kostenlos Anfrage erstellen', action: 'customer' },
  secondaryCta: { label: 'Erst Anlagengröße berechnen', action: 'link', href: '/pv-rechner' },
  relatedLinks: [
    { label: 'Photovoltaik-Anfrage erstellen', href: '/photovoltaik-anfrage-erstellen' },
    { label: 'So funktioniert der Angebotsvergleich', href: '/pv-angebot-vergleichen' },
    { label: 'Anfrage ganz ohne Telefonnummer oder E-Mail', href: '/photovoltaik-ohne-telefonnummer' },
    // GitLab-Issue #144: gegenseitige Verlinkung der beiden How-to-Guides, wie in #142 gefordert.
    { label: 'So funktioniert Roofora für Handwerksbetriebe', href: '/so-funktioniert-es-fuer-handwerker' },
  ],
}
